import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ESTIMATOR_STEPS, TIMELINES } from "../config/estimator.config";
import { computeRange } from "../config/pricing.config";
import { countyForZip } from "../config/serviceArea.config";
import { formatRange } from "../lib/format";
import {
  submitLead,
  readUtm,
  newEventId,
  type BathroomScope,
  type Timeline,
} from "../lib/lead";
import {
  trackViewContent,
  trackLeadFormStarted,
  trackLead,
  getCookie,
} from "../lib/pixel";
import BookingStep from "../components/BookingStep";

type Sel = Record<string, string>;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Niveles del estudio (baño de lujo). "under" = below-min → gate graceful. "guide" = califica. */
type TierId = "signature" | "spa" | "estate" | "under" | "guide";
const TIER_OPTIONS: { id: TierId; label: string; range?: string; sub: string; emoji: string }[] = [
  { id: "signature", label: "Signature Refresh", range: "$25–40K", sub: "A refined, full redesign of one bath", emoji: "◔" },
  { id: "spa", label: "Spa / Primary Suite", range: "$40–70K", sub: "A primary bath that feels like a retreat", emoji: "◑" },
  { id: "estate", label: "Estate Bath", range: "$70K+", sub: "No ceiling — stone, steam, freestanding tub", emoji: "●" },
  { id: "under", label: "A simpler update", range: "Under $25K", sub: "A lighter refresh for now", emoji: "◌" },
  { id: "guide", label: "Guide me to the right level", sub: "Help me scope it", emoji: "🤔" },
];
const TIER_BAND: Record<TierId, "A" | "B" | "C" | null> = { signature: "A", spa: "B", estate: "C", under: "A", guide: null };
const TIER_VALUE: Record<TierId, number> = { signature: 32500, spa: 55000, estate: 85000, under: 18000, guide: 45000 };

export default function Estimator() {
  const nav = useNavigate();
  const [q, setQ] = useState(0); // índice dentro de los pasos VISIBLES (último = timeline)
  const [sel, setSel] = useState<Sel>({});
  const [timeline, setTimeline] = useState<Timeline>(null);
  const [phase, setPhase] = useState<"wizard" | "tier" | "contact" | "revealed">("wizard");
  const [contact, setContact] = useState({ first_name: "", phone: "", email: "", zip: "" });
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<{ range: string } | null>(null);
  const [flexible, setFlexible] = useState(false); // escape hatch del gate de lujo
  const [tier, setTier] = useState<TierId | null>(null); // nivel del estudio (gate + reveal)

  const started = useRef(false);
  useEffect(() => trackViewContent("estimator"), []);

  // Pasos visibles según las selecciones (algunos dependen del scope elegido)
  const visible = useMemo(
    () => ESTIMATOR_STEPS.filter((s) => !s.showIf || s.showIf(sel)),
    [sel],
  );
  const total = visible.length + 3; // pasos visibles + timeline + tier + contacto

  const { min, max } = useMemo(() => {
    const mins: number[] = [];
    const maxs: number[] = [];
    for (const step of ESTIMATOR_STEPS) {
      const opt = step.options.find((o) => o.id === sel[step.key]);
      if (opt) {
        mins.push(opt.price[0]);
        maxs.push(opt.price[1]);
      }
    }
    return computeRange(mins, maxs);
  }, [sel]);
  const range = formatRange(min, max);
  const hasPrice = max > 0;
  const selectedTier = TIER_OPTIONS.find((t) => t.id === tier);
  // Gate de lujo: lo decide el NIVEL que el cliente eligió (no el número calculado).
  const qualified = tier !== null && tier !== "under";
  const showRange = tier === "signature" || tier === "spa" || tier === "estate";

  function markStarted() {
    if (!started.current) {
      started.current = true;
      trackLeadFormStarted("estimator");
    }
  }

  function pickOption(key: string, id: string) {
    markStarted();
    setSel((s) => {
      const next = { ...s, [key]: id };
      // Avanza al siguiente paso visible calculado con la selección nueva
      const vis = ESTIMATOR_STEPS.filter((st) => !st.showIf || st.showIf(next));
      setTimeout(() => setQ((n) => Math.min(n + 1, vis.length)), 190);
      return next;
    });
  }

  function pickTimeline(id: Timeline) {
    setTimeline(id);
    setTimeout(() => setPhase("tier"), 190);
  }

  function pickTier(id: TierId) {
    setTier(id);
    setTimeout(() => setPhase("contact"), 190);
  }

  function goBack() {
    if (phase === "contact") return setPhase("tier");
    if (phase === "tier") return setPhase("wizard");
    if (q === 0) return nav("/");
    setQ((n) => n - 1);
  }

  function buildScope(): BathroomScope {
    const scope: BathroomScope = {};
    for (const step of visible) {
      const v = sel[step.key];
      if (v) (scope as Record<string, string>)[step.key] = v;
    }
    scope.extras = [];
    return scope;
  }

  async function submit() {
    const e: Record<string, string> = {};
    if (!contact.first_name.trim()) e.first_name = "Your name?";
    if (!/\d{10,}/.test(contact.phone.replace(/\D/g, ""))) e.phone = "Valid phone?";
    // Email OPCIONAL — solo validamos si escribieron algo.
    if (contact.email && !EMAIL_RE.test(contact.email)) e.email = "Valid email?";
    if (!countyForZip(contact.zip)) e.zip = "We serve Pinellas · Hillsborough · Pasco";
    if (!consent) e.consent = "Please agree to be contacted.";
    setErrors(e);
    if (Object.keys(e).length) return;

    setSubmitting(true);
    setErr(null);
    const eventId = newEventId();
    const res = await submitLead({
      lead_source: "estimator",
      first_name: contact.first_name,
      phone: contact.phone,
      email: contact.email,
      address: "",
      zip: contact.zip,
      bathroom_scope: buildScope(),
      investment_band: tier ? TIER_BAND[tier] : null,
      estimator_range: selectedTier?.range ?? null,
      timeline,
      consent,
      qualified,
      budget_flexible: flexible,
      utm: readUtm(),
      event_id: eventId,
      fbp: getCookie("_fbp"),
      fbc: getCookie("_fbc"),
      page_url: typeof window !== "undefined" ? window.location.href : undefined,
    });
    setSubmitting(false);
    if (res.ok) {
      trackLead(eventId, tier ? TIER_VALUE[tier] : 0);
      setRevealed({ range: selectedTier?.range ?? "" });
    } else setErr("Something went wrong. Please try again.");
  }

  /* ─── GATE: below the $25K floor → graceful disqualify (contacto ya capturado) ─── */
  if (revealed && !qualified && !flexible) {
    return (
      <main className="flex min-h-[100dvh] flex-col bg-bone">
        <div className="relative h-40 w-full overflow-hidden sm:h-52">
          <img src="/img/styles/spa.webp" alt="" className="absolute inset-0 h-full w-full object-cover" />
          <span className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-bone" />
        </div>
        <div className="mx-auto -mt-12 max-w-md px-6 pb-16">
          <div className="rounded-3xl border border-stone/15 bg-white/85 p-8 text-center shadow-sm backdrop-blur">
            <p className="font-sans text-[11px] uppercase tracking-[0.25em] text-clay">
              Thank you for sharing your project
            </p>
            <h1 className="mt-3 font-serif text-3xl leading-tight text-ink">
              Let&rsquo;s make sure we&rsquo;re the right fit.
            </h1>
            <p className="mt-4 leading-relaxed text-stone">
              Our design-and-build studio focuses on full bathroom renovations from
              <strong> $25,000 and up</strong> — the level where design leadership and one
              accountable team truly change the result. Based on what you shared, your
              project sits just below that today.
            </p>
            <p className="mt-3 leading-relaxed text-stone">
              If your plans grow, we&rsquo;d love to hear from you — you&rsquo;re already on our
              list, and Ana Clara &amp; Javier will keep you in mind.
            </p>
            <button
              onClick={() => setFlexible(true)}
              className="mt-6 inline-block text-sm font-semibold uppercase tracking-widest text-clay underline underline-offset-4 transition hover:text-ink"
            >
              My budget is flexible →
            </button>
          </div>
          <p className="mt-5 text-center text-[11px] text-stone/70">
            — Ana Clara &amp; Javier · A Couple &amp; A Dream
          </p>
        </div>
      </main>
    );
  }

  /* ─── REVEAL (qualified · o escape hatch) ─────────────────── */
  if (revealed) {
    return (
      <main className="min-h-[100dvh] bg-bone">
        {/* Foto */}
        <div className="relative h-44 w-full overflow-hidden sm:h-56">
          <img src="/img/styles/spa.webp" alt="" className="absolute inset-0 h-full w-full object-cover" />
          <span className="absolute inset-0 bg-gradient-to-b from-black/25 via-transparent to-bone" />
        </div>

        <div className="mx-auto -mt-14 max-w-md px-5 pb-16">
          {/* Rango — o, si entró por el escape hatch bajo el mínimo, "lo dimensionamos juntos" */}
          <div className="rounded-3xl border border-stone/15 bg-white/80 p-7 text-center shadow-sm backdrop-blur">
            {showRange ? (
              <>
                <p className="font-sans text-[11px] uppercase tracking-[0.25em] text-clay">
                  Your level of the studio
                </p>
                <h1 className="mt-2 font-serif text-3xl leading-tight text-ink">{selectedTier?.label}</h1>
                <p className="mt-1 font-serif text-4xl leading-none text-clay">{selectedTier?.range}</p>
                <p className="mx-auto mt-3 max-w-xs text-xs leading-relaxed text-stone">
                  All-in: design, construction, and a curated finishes allowance. We confirm
                  the exact number together once your selections are approved.
                </p>
              </>
            ) : (
              <>
                <p className="font-sans text-[11px] uppercase tracking-[0.25em] text-clay">
                  Let&rsquo;s scope it together
                </p>
                <h1 className="mt-2 font-serif text-3xl leading-tight text-ink">
                  A plan built around your home.
                </h1>
                <p className="mx-auto mt-3 max-w-xs text-xs leading-relaxed text-stone">
                  Since you&rsquo;re open on budget, we&rsquo;ll shape the plan and the number
                  with you on the visit. Our design-and-build renovations begin at $25,000.
                </p>
              </>
            )}
          </div>

          {/* Voz A Couple & A Dream */}
          <div className="mt-9 text-center">
            <h2 className="font-serif text-3xl leading-tight text-ink">
              This is where it gets exciting.
            </h2>
            <p className="mt-3 leading-relaxed text-stone">
              We're Ana Clara &amp; Javier — a couple who design <em>and</em> build every
              bathroom ourselves. One studio, one team, from the first render to the last
              tile. Just the retreat you keep picturing, finally made real.
            </p>
            <p className="mt-3 font-serif text-sm italic text-clay">
              — Javier &amp; Ana Clara · A Couple &amp; A Dream
            </p>
          </div>

          {/* CTA */}
          <div className="mt-9 rounded-3xl border border-clay/30 bg-clay/[0.06] p-6 text-center">
            <p className="font-sans text-[11px] uppercase tracking-[0.25em] text-clay">
              Your next step
            </p>
            <h3 className="mt-1 font-serif text-2xl text-ink">Book your in-home design visit</h3>
            <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-stone">
              Ana Clara &amp; Javier come to you, see the space, and turn this range into a
              real, buildable plan. We take only a few bathrooms a month.
            </p>
            <div className="mt-5">
              <BookingStep value={tier ? TIER_VALUE[tier] : 0} hideHeader />
            </div>
          </div>
        </div>
      </main>
    );
  }

  /* ─── CONTACT ────────────────────────────────────────────── */
  if (phase === "contact") {
    const county = countyForZip(contact.zip);
    return (
      <Shell step={total - 1} total={total} onBack={goBack}>
        <div className="mx-auto w-full max-w-sm">
          {/* Tease del rango: crea deseo — estás a 1 paso de verlo */}
          {selectedTier && tier !== "guide" && (
            <div className="mb-5 rounded-2xl border border-clay/30 bg-clay/[0.06] px-5 py-4 text-center">
              <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-clay">
                Your level
              </p>
              <p className="mt-1 font-serif text-2xl text-ink">
                {selectedTier.label}{selectedTier.range ? ` · ${selectedTier.range}` : ""}
              </p>
            </div>
          )}
          <h1 className="text-center font-serif text-3xl text-ink">
            Where should we send it?
          </h1>
          <p className="mt-2 text-center text-sm text-stone">
            Takes 10 seconds. We'll show your range and, if it's a fit, times to have
            Ana Clara &amp; Javier walk your space.
          </p>
          <div className="mt-6 space-y-3">
            <Inp ph="First name" v={contact.first_name} on={(v) => setContact((c) => ({ ...c, first_name: v }))} e={errors.first_name} />
            <Inp ph="Phone" v={contact.phone} on={(v) => setContact((c) => ({ ...c, phone: v }))} e={errors.phone} mode="tel" />
            <Inp ph="ZIP code" v={contact.zip} on={(v) => setContact((c) => ({ ...c, zip: v }))} e={errors.zip} mode="numeric" hint={county ? `${county} County ✓` : undefined} />
            <Inp ph="Email (optional)" v={contact.email} on={(v) => setContact((c) => ({ ...c, email: v }))} e={errors.email} mode="email" />
            <label className="flex items-start gap-2 pt-1 text-xs text-stone">
              <input type="checkbox" className="mt-0.5" checked={consent} onChange={(ev) => setConsent(ev.target.checked)} />
              <span>I agree that A Couple &amp; A Dream may contact me by call, text and email. Message/data rates may apply.</span>
            </label>
            {errors.consent && <p className="text-xs text-clay">{errors.consent}</p>}
            <button
              onClick={submit}
              disabled={submitting}
              className="mt-2 w-full rounded-xl bg-ink py-4 font-sans text-sm font-semibold uppercase tracking-widest text-bone transition hover:bg-clay disabled:opacity-60"
            >
              {submitting ? "One moment…" : "Show my range →"}
            </button>
            <p className="text-center text-[11px] text-stone/70">🔒 No spam. No obligation.</p>
            {err && <p className="text-center text-sm text-clay">{err}</p>}
          </div>
        </div>
      </Shell>
    );
  }

  /* ─── TIER (nivel del estudio) — el gate + el reveal se anclan aquí ─── */
  if (phase === "tier") {
    return (
      <Shell step={visible.length + 1} total={total} onBack={goBack}>
        <h1 className="text-center font-serif text-3xl text-ink">
          Where does this project sit for you?
        </h1>
        <p className="mx-auto mt-2 max-w-xs text-center text-sm text-stone">
          So we match you to the right level of the studio. Our design-and-build
          renovations begin at $25,000.
        </p>
        <div className="mx-auto mt-6 w-full max-w-sm space-y-3">
          {TIER_OPTIONS.map((t) => (
            <button
              key={t.id}
              onClick={() => pickTier(t.id)}
              className="flex w-full items-center gap-4 rounded-2xl border-2 border-stone/20 bg-white/60 px-5 py-4 text-left transition hover:border-clay"
            >
              <span className="text-2xl leading-none">{t.emoji}</span>
              <span className="flex-1">
                <span className="block font-serif text-xl text-ink">{t.label}</span>
                <span className="block text-[11px] uppercase tracking-wide text-stone">{t.sub}</span>
              </span>
              {t.range && <span className="font-serif text-sm text-clay">{t.range}</span>}
            </button>
          ))}
        </div>
      </Shell>
    );
  }

  /* ─── TIMELINE ───────────────────────────────────────────── */
  if (q >= visible.length) {
    return (
      <Shell step={visible.length} total={total} onBack={goBack} range={hasPrice ? range : undefined}>
        <h1 className="text-center font-serif text-3xl text-ink">
          When would you start?
        </h1>
        <div className="mx-auto mt-6 w-full max-w-sm space-y-3">
          {TIMELINES.map((t) => (
            <button
              key={t.id}
              onClick={() => pickTimeline(t.id as Timeline)}
              className="flex w-full items-center gap-4 rounded-2xl border-2 border-stone/20 bg-white/60 px-5 py-4 text-left transition hover:border-clay"
            >
              <span className="text-2xl">{t.emoji}</span>
              <span className="font-serif text-xl text-ink">{t.label}</span>
            </button>
          ))}
        </div>
      </Shell>
    );
  }

  /* ─── WIZARD STEP (image grid) ───────────────────────────── */
  const step = visible[q];
  return (
    <Shell step={q} total={total} onBack={goBack} range={hasPrice ? range : undefined}>
      {q === 0 ? (
        <div className="mx-auto max-w-md text-center">
          <p className="font-sans text-[11px] uppercase tracking-[0.25em] text-clay">
            Private · 60 seconds · A few homes a month
          </p>
          <h1 className="mt-2 font-serif text-3xl leading-tight text-ink sm:text-4xl">
            Let&rsquo;s design your master bath.
          </h1>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-stone">
            A few quick taps and we&rsquo;ll show you a real all-in range for a
            design-and-build renovation. Our bathrooms begin at $25,000.
          </p>
          <div className="mx-auto mt-4 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[11px] text-stone">
            <span className="font-semibold text-ink">★★★★★ 4.9</span>
            <span className="text-clay/50">•</span>
            <span>Designed &amp; built by Ana Clara &amp; Javier</span>
            <span className="text-clay/50">•</span>
            <span>Pinellas · Hillsborough · Pasco</span>
          </div>
          <h2 className="mt-7 font-serif text-2xl text-ink">
            Let&rsquo;s start — which bathroom are we reimagining?
          </h2>
        </div>
      ) : (
        <>
          <h1 className="text-center font-serif text-3xl text-ink">{step.title}</h1>
          {step.help && (
            <p className="mx-auto mt-2 max-w-xs text-center text-sm text-stone">{step.help}</p>
          )}
        </>
      )}
      <div
        className={
          "mx-auto mt-5 grid w-full max-w-lg gap-3 " +
          (step.cols === 3 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2")
        }
      >
        {step.options.map((opt, i) => {
          const active = sel[step.key] === opt.id;
          // Si el total es impar, la última queda sola en la fila → centrarla (solo en la grilla móvil de 2 columnas).
          const orphan = step.options.length % 2 === 1 && i === step.options.length - 1;
          return (
            <button
              key={opt.id}
              onClick={() => pickOption(step.key, opt.id)}
              className={
                "group relative aspect-[4/3] overflow-hidden rounded-2xl border-2 transition " +
                (orphan ? "max-sm:col-span-2 max-sm:mx-auto max-sm:w-[calc(50%-0.375rem)] " : "") +
                (active ? "border-clay ring-2 ring-clay" : "border-transparent hover:border-clay/50")
              }
            >
              <img src={opt.img} alt={opt.label} className="absolute inset-0 h-full w-full object-cover" loading="eager" />
              <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              {opt.badge && !active && (
                <span className="absolute left-2 top-2 rounded-full bg-clay px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm">
                  {opt.badge}
                </span>
              )}
              {active && (
                <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-clay text-sm font-bold text-white">
                  ✓
                </span>
              )}
              <span className="absolute inset-x-0 bottom-0 p-3 text-left text-white">
                <span className="block font-serif text-lg leading-tight">{opt.label}</span>
                {opt.sub && <span className="block text-[11px] uppercase tracking-wide opacity-90">{opt.sub}</span>}
              </span>
            </button>
          );
        })}
      </div>
    </Shell>
  );
}

/* ── Shell: barra superior de progreso + barra inferior de rango en vivo ── */
function Shell({
  step,
  total,
  onBack,
  range,
  children,
}: {
  step: number;
  total: number;
  onBack: () => void;
  range?: string;
  children: React.ReactNode;
}) {
  const pct = Math.round(((step + 1) / total) * 100);
  return (
    <main className="flex min-h-[100dvh] flex-col bg-bone">
      <div className="px-5 pt-5">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <button onClick={onBack} aria-label="Back" className="text-2xl leading-none text-stone hover:text-ink">
            ←
          </button>
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-stone/15">
            <div className="h-full rounded-full bg-clay transition-all duration-300" style={{ width: `${pct}%` }} />
          </div>
          <span className="w-10 text-right text-xs tabular-nums text-stone">{step + 1}/{total}</span>
        </div>
      </div>

      <div className="flex flex-1 flex-col justify-center px-5 py-6">{children}</div>

      {range && (
        <div className="sticky bottom-0 border-t border-stone/15 bg-bone/95 px-5 py-3 backdrop-blur">
          <div className="mx-auto flex max-w-lg items-center justify-between">
            <span className="text-xs uppercase tracking-wide text-stone">Building your plan</span>
            <span className="select-none font-serif text-xl text-ink blur-[7px]">{range}</span>
          </div>
        </div>
      )}
    </main>
  );
}

function Inp({
  ph,
  v,
  on,
  e,
  mode,
  hint,
}: {
  ph: string;
  v: string;
  on: (v: string) => void;
  e?: string;
  mode?: "tel" | "email" | "numeric";
  hint?: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <input
          value={v}
          onChange={(ev) => on(ev.target.value)}
          placeholder={ph}
          inputMode={mode}
          className="w-full rounded-xl border border-stone/30 bg-white px-4 py-3.5 text-ink outline-none transition placeholder:text-stone/60 focus:border-clay focus:ring-1 focus:ring-clay"
        />
      </div>
      {hint && <p className="mt-1 text-xs text-sage">{hint}</p>}
      {e && <p className="mt-1 text-xs text-clay">{e}</p>}
    </div>
  );
}
